'use client';
import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { subscribeToMailingBoss } from '../../lib/mailingboss';
import { formatCnpj, isPersonalEmail, maskCnpj, PERSONAL_EMAIL_MESSAGE } from '../../lib/validation/companyRegister';
import { measureCustomEvent } from '../OpenAIAdsPixel/oaiq';

const PLAN_VALUES = ['bronze', 'silver', 'gold'] as const;
const FALE_CONOSCO_ID = 'fale-conosco';
const COMMERCIAL_WHATSAPP = '5551980906991';
const COMMERCIAL_WHATSAPP_MESSAGE = encodeURIComponent(
    'Olá! Acabei de cadastrar minha empresa na IncluiPcD e gostaria de falar com o time comercial.'
);
const COMMERCIAL_WHATSAPP_URL = `https://wa.me/${COMMERCIAL_WHATSAPP}?text=${COMMERCIAL_WHATSAPP_MESSAGE}`;

const WhatsAppIcon: FC<{ size?: number; color?: string }> = ({ size = 18, color = '#25D366' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <path
            fill={color}
            d="M12.04 2C6.58 2 2.14 6.34 2.14 11.7c0 1.9.56 3.75 1.62 5.33L2 22l5.12-1.68c1.52.82 3.23 1.25 4.95 1.25h.01c5.46 0 9.9-4.34 9.9-9.69C21.98 6.34 17.54 2 12.04 2Zm5.78 14.06c-.24.67-1.38 1.28-1.9 1.35-.48.07-1.1.1-1.77-.11-.41-.13-.93-.3-1.61-.59-2.83-1.2-4.68-4.13-4.82-4.32-.13-.18-1.15-1.52-1.15-2.9 0-1.38.73-2.06.99-2.35.26-.29.57-.36.76-.36.18 0 .37 0 .54.01.17.01.39-.06.61.46.24.59.81 2.03.88 2.18.07.15.11.33.02.51-.09.18-.13.33-.27.5-.13.18-.28.39-.4.52-.13.13-.26.28-.11.55.15.26.67 1.1 1.44 1.78.99.88 1.82 1.15 2.08 1.28.26.13.41.11.56-.07.15-.18.64-.74.81-1 .17-.26.33-.22.55-.13.22.09 1.4.66 1.64.78.24.13.41.18.46.28.06.1.06.62-.18 1.29Z"
        />
    </svg>
);

const PLAN_LABEL_BY_VALUE = {
    bronze: 'Plataforma Bronze',
    silver: 'Plataforma Silver',
    gold: 'Plataforma Gold',
    custom: 'Personalizado',
} as const;

const normalizePhone = (value: string): string => value.replace(/\D/g, '').slice(0, 11);

/** Format digits into (DD) 9DDDD-DDDD or (DD) DDDD-DDDD */
const formatPhone = (raw: string): string => {
    const d = raw.replace(/\D/g, '');
    if (d.length === 0) return '';
    if (d.length <= 10) {
        // (DD) DDDD-DDDD
        const area = d.slice(0, 2);
        const part1 = d.slice(2, 6);
        const part2 = d.slice(6);
        return part2 ? `(${area}) ${part1}-${part2}` : part1 ? `(${area}) ${part1}` : `(${area})`;
    }
    // (DD) DDDDD-DDDD
    const area = d.slice(0, 2);
    const part1 = d.slice(2, 7);
    const part2 = d.slice(7);
    return part2 ? `(${area}) ${part1}-${part2}` : part1 ? `(${area}) ${part1}` : `(${area})`;
};

const ContactForm: FC = () => {
    const searchParams = useSearchParams();
    const [fullName, setFullName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [phone, setPhone] = useState('');
    const [position, setPosition] = useState('');
    const [numEmployees, setNumEmployees] = useState('');
    const [cnpj, setCnpj] = useState('');
    const [interestPlan, setInterestPlan] = useState('');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [feedbackError, setFeedbackError] = useState(false);
    const [emailError, setEmailError] = useState('');
    const planoFromQuery = searchParams.get('plano');

    // Captura UTMs da URL (ex.: ?utm_source=googleads&utm_medium=cadastroempresa&utm_campaign=incluipcd)
    // para encaminhá-las no cadastro da empresa, seguindo o mesmo padrão do fluxo de candidato.
    const utmParams = useMemo(() => {
        const params: Record<string, string> = {};
        searchParams.forEach((value, key) => {
            if (key.startsWith('utm_')) {
                params[key] = value;
            }
        });
        return params;
    }, [searchParams]);

    useEffect(() => {
        if (!planoFromQuery || !PLAN_VALUES.includes(planoFromQuery as (typeof PLAN_VALUES)[number])) {
            return;
        }

        setInterestPlan(planoFromQuery);
        document.getElementById(FALE_CONOSCO_ID)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, [planoFromQuery]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFeedbackMessage('');
        setFeedbackError(false);

        // E-mail pessoal é recusado pelo backend. Barrar aqui evita a ida e volta
        // e deixa o motivo colado no campo, e não só no rodapé do formulário.
        if (isPersonalEmail(email)) {
            setEmailError(PERSONAL_EMAIL_MESSAGE);
            setFeedbackError(true);
            setFeedbackMessage(PERSONAL_EMAIL_MESSAGE);
            return;
        }

        setSubmitting(true);

        try {
            const payload = {
                companyName,
                contactName: fullName,
                position,
                cnpj,
                numEmployees,
                plan: PLAN_LABEL_BY_VALUE[interestPlan as keyof typeof PLAN_LABEL_BY_VALUE] ?? 'Personalizado',
                email,
                whatsapp: formatPhone(whatsapp),
                phone: phone ? formatPhone(phone) : '',
                message,
                ...utmParams,
            };

            const response = await fetch('/api/v2/company/lead', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
                //@ts-ignore-next-line
                duplex: 'half',
                mode: 'cors',
                cache: 'no-cache',
                credentials: 'same-origin',
                redirect: 'follow',
            });

            const data = await response.json().catch(() => null);

            // O backend responde HTTP 200 com {success:false} nos erros de negócio
            // (e-mail já cadastrado, falha ao gravar no Konecty). Olhar só o status HTTP
            // mostrava "Cadastro realizado com sucesso" sem nada ter sido criado.
            if (!response.ok || data?.success === false) {
                const firstError = Array.isArray(data?.errors) ? data.errors[0] : 'Não foi possível enviar sua solicitação.';
                throw new Error(firstError);
            }

            subscribeToMailingBoss('company', {
                fullname: companyName,
                contactname: fullName,
                email,
                whatsapp: formatPhone(whatsapp),
                cnpj,
                ...utmParams,
            });

            // Conversão do ChatGPT Ads: lead comercial de empresa (task #4630).
            measureCustomEvent('company_registered');

            setFeedbackMessage('Solicitação enviada com sucesso! Em breve entraremos em contato.');
            setSuccess(true);
            setFullName('');
            setCompanyName('');
            setEmail('');
            setWhatsapp('');
            setPhone('');
            setPosition('');
            setNumEmployees('');
            setCnpj('');
            setInterestPlan('');
            setMessage('');
            setEmailError('');
        } catch (error) {
            setFeedbackError(true);
            setFeedbackMessage(error instanceof Error ? error.message : 'Não foi possível enviar sua solicitação.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id={FALE_CONOSCO_ID}
            className="relative overflow-hidden"
            style={{
                padding: '80px 0',
                background: 'linear-gradient(135deg, #0F1DD7 0%, #1e40af 50%, #1e3a8a 100%)',
            }}
        >
            {/* Decorative blobs */}
            <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-400/20 blur-[120px] rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue-300/10 blur-[100px] rounded-full translate-x-1/2 translate-y-1/2 pointer-events-none" />

            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
                {success ? (
                    <div className="bg-white rounded-3xl p-10 md:p-16 text-center shadow-2xl max-w-3xl mx-auto border border-blue-100/50">
                        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                            </svg>
                        </div>
                        <h2 className="text-4xl font-bold text-[#0A1647] mb-6">Cadastro realizado com sucesso!</h2>
                        <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                            Você receberá um e-mail confirmando sua inscrição. Se preferir agilizar o atendimento, fale agora com o time comercial pelo WhatsApp.
                        </p>
                        <div className="flex flex-col items-center justify-center gap-4 px-4 sm:px-6">
                            <a
                                href={COMMERCIAL_WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#25D366] hover:bg-[#1fb857] text-white font-bold py-5 px-10 rounded-full transition-all shadow-lg hover:shadow-green-500/30 inline-flex items-center justify-center gap-3 text-xl whitespace-nowrap w-full"
                            >
                                <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded-full bg-white shrink-0 p-1">
                                    <WhatsAppIcon />
                                </span>
                                <span>Conversar pelo WhatsApp</span>
                            </a>
                            <Link
                                href="/"
                                className="text-[#0F1DD7] hover:text-blue-700 font-bold py-3 px-8 rounded-2xl transition-all inline-flex items-center justify-center text-lg"
                            >
                                Voltar para o site
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col lg:flex-row items-start gap-16">

                    {/* Left column */}
                    <div className="lg:w-1/2 text-left">
                        <div
                            className="inline-block text-white font-medium mb-6 rounded-full border border-white/30"
                            style={{ padding: '6px 16px', fontSize: '14px', background: 'rgba(255,255,255,0.15)' }}
                        >
                            Fale Conosco
                        </div>
                        <h2 className="text-white font-bold mb-6" style={{ fontSize: '36px', lineHeight: '1.2' }}>
                            Pronto para transformar sua empresa?
                        </h2>
                        <p className="text-white/80 leading-relaxed" style={{ fontSize: '18px', maxWidth: '440px' }}>
                            Entre em contato com nossa equipe de especialistas e descubra como podemos ajudar sua empresa a construir um ambiente verdadeiramente inclusivo.
                        </p>
                    </div>

                    {/* Right column - Form card */}
                    <div className="lg:w-1/2 w-full">
                        <div
                            className="bg-white w-full"
                            style={{ borderRadius: '16px', padding: '32px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
                        >
                            <h3 className="text-[#111827] font-bold mb-6" style={{ fontSize: '22px' }}>
                                Solicite uma demonstração
                            </h3>

                            <form className="flex flex-col gap-5" onSubmit={handleSubmit}>

                                {/* Row 1: Nome + Email */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div className="flex flex-col gap-1">
                                        <label htmlFor="contato-nome" className="text-[#374151] font-medium" style={{ fontSize: '14px' }}>
                                            Nome completo <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="contato-nome"
                                            type="text"
                                            placeholder="Seu nome"
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            required
                                            className="border border-[#E5E7EB] rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            style={{ padding: '10px 14px', fontSize: '14px', color: '#374151', background: '#fff' }}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label htmlFor="contato-email" className="text-[#374151] font-medium" style={{ fontSize: '14px' }}>
                                            E-mail corporativo <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="contato-email"
                                            type="email"
                                            placeholder="voce@suaempresa.com.br"
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                                if (emailError) setEmailError('');
                                            }}
                                            onBlur={(e) => setEmailError(isPersonalEmail(e.target.value) ? PERSONAL_EMAIL_MESSAGE : '')}
                                            required
                                            aria-invalid={emailError ? true : undefined}
                                            aria-describedby="contato-email-ajuda"
                                            className={`border rounded-lg outline-none focus:ring-2 focus:border-transparent transition-all ${
                                                emailError ? 'border-red-500 focus:ring-red-500' : 'border-[#E5E7EB] focus:ring-blue-500'
                                            }`}
                                            style={{ padding: '10px 14px', fontSize: '14px', color: '#374151', background: '#fff' }}
                                        />
                                        <p
                                            id="contato-email-ajuda"
                                            style={{ fontSize: '12px', color: emailError ? '#ef4444' : '#6B7280' }}
                                        >
                                            {emailError || 'Precisa ser o e-mail corporativo da empresa. Gmail, Hotmail, Yahoo e similares não são aceitos.'}
                                        </p>
                                    </div>
                                </div>

                                {/* Row 2: Empresa (Full width) */}
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="contato-empresa" className="text-[#374151] font-medium" style={{ fontSize: '14px' }}>
                                        Empresa <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="contato-empresa"
                                        type="text"
                                        placeholder="Nome da empresa"
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        required
                                        className="border border-[#E5E7EB] rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        style={{ padding: '10px 14px', fontSize: '14px', color: '#374151', background: '#fff' }}
                                    />
                                </div>

                                {/* Row 3: WhatsApp + Telefone */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div className="flex flex-col gap-1">
                                        <label htmlFor="contato-whatsapp" className="text-[#374151] font-medium" style={{ fontSize: '14px' }}>
                                            WhatsApp <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="contato-whatsapp"
                                            type="tel"
                                            placeholder="(11) 99999-9999"
                                            value={whatsapp}
                                            onChange={(e) => setWhatsapp(normalizePhone(e.target.value))}
                                            required
                                            className="border border-[#E5E7EB] rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            style={{ padding: '10px 14px', fontSize: '14px', color: '#374151', background: '#fff' }}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label htmlFor="contato-telefone" className="text-[#374151] font-medium" style={{ fontSize: '14px' }}>
                                            Telefone
                                        </label>
                                        <input
                                            id="contato-telefone"
                                            type="tel"
                                            placeholder="(11) 99999-9999"
                                            value={phone}
                                            onChange={(e) => setPhone(normalizePhone(e.target.value))}
                                            className="border border-[#E5E7EB] rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            style={{ padding: '10px 14px', fontSize: '14px', color: '#374151', background: '#fff' }}
                                        />
                                    </div>
                                </div>

                                {/* Row 3: Cargo + N° de funcionários */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div className="flex flex-col gap-1">
                                        <label htmlFor="contato-cargo" className="text-[#374151] font-medium" style={{ fontSize: '14px' }}>
                                            Cargo <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="contato-cargo"
                                            type="text"
                                            placeholder="Seu cargo"
                                            value={position}
                                            onChange={(e) => setPosition(e.target.value)}
                                            required
                                            className="border border-[#E5E7EB] rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            style={{ padding: '10px 14px', fontSize: '14px', color: '#374151', background: '#fff' }}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label htmlFor="contato-funcionarios" className="text-[#374151] font-medium" style={{ fontSize: '14px' }}>
                                            Nº de funcionários <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            id="contato-funcionarios"
                                            value={numEmployees}
                                            onChange={(e) => setNumEmployees(e.target.value)}
                                            required
                                            className="border border-[#E5E7EB] rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none"
                                            style={{ padding: '10px 14px', fontSize: '14px', color: '#374151', background: '#fff' }}
                                        >
                                            <option value="" disabled hidden>Selecione...</option>
                                        <option value="0-99">0 a 99 funcionários</option>
                                        <option value="100-200">100 a 200 funcionários</option>
                                        <option value="201-500">201 a 500 funcionários</option>
                                        <option value="501-1000">501 a 1.000 funcionários</option>
                                        <option value="1001-5000">1001 a 5.000 funcionários</option>
                                        <option value="5001-10000">5.001 a 10.000 funcionários</option>
                                        <option value="10000+">+ de 10.000 funcionários</option>
                                    </select>
                                    </div>
                                </div>

                                {/* CNPJ */}
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="contato-cnpj" className="text-[#374151] font-medium" style={{ fontSize: '14px' }}>
                                        CNPJ <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="contato-cnpj"
                                        type="text"
                                        placeholder="00.000.000/0000-00"
                                        value={maskCnpj(cnpj)}
                                        onChange={(e) => setCnpj(formatCnpj(e.target.value))}
                                        required
                                        className="border border-[#E5E7EB] rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        style={{ padding: '10px 14px', fontSize: '14px', color: '#374151', background: '#fff', textTransform: 'uppercase' }}
                                    />
                                </div>

                                {/* Plano de interesse */}
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="contato-plano" className="text-[#374151] font-medium" style={{ fontSize: '14px' }}>
                                        Plano de interesse <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        id="contato-plano"
                                        value={interestPlan}
                                        onChange={(e) => setInterestPlan(e.target.value)}
                                        required
                                        className="border border-[#E5E7EB] rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none"
                                        style={{ padding: '10px 14px', fontSize: '14px', color: '#374151', background: '#fff' }}
                                    >
                                        <option value="" disabled hidden>Selecione o plano</option>
                                        <option value="bronze">Plano Bronze</option>
                                        <option value="silver">Plano Silver</option>
                                        <option value="gold">Plano Gold</option>
                                        <option value="custom">Personalizado</option>
                                    </select>
                                </div>

                                {/* Mensagem */}
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="contato-mensagem" className="text-[#374151] font-medium" style={{ fontSize: '14px' }}>
                                        Mensagem (opcional)
                                    </label>
                                    <textarea
                                        id="contato-mensagem"
                                        rows={4}
                                        placeholder="Conte-nos mais sobre suas necessidades..."
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        className="border border-[#E5E7EB] rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                                        style={{ padding: '10px 14px', fontSize: '14px', color: '#374151', background: '#fff' }}
                                    />
                                </div>



                                {feedbackMessage ? (
                                    <div role="alert" style={{ marginTop: '4px' }}>
                                        <p style={{ fontSize: '14px', color: feedbackError ? '#ef4444' : '#10b981' }}>
                                            {feedbackMessage}
                                        </p>
                                        {/* Página de conversão: erro não pode ser beco sem saída. */}
                                        {feedbackError ? (
                                            <a
                                                href={COMMERCIAL_WHATSAPP_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-3 bg-[#25D366] hover:bg-[#1fb857] text-white font-bold py-3 px-6 rounded-full transition-all inline-flex items-center justify-center gap-2 text-sm"
                                            >
                                                <span aria-hidden="true" className="flex h-6 w-6 items-center justify-center rounded-full bg-white shrink-0 p-1">
                                                    <WhatsAppIcon size={14} />
                                                </span>
                                                <span>Falar com o time comercial</span>
                                            </a>
                                        ) : null}
                                    </div>
                                ) : null}

                                {/* Submit button */}
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full flex items-center justify-center gap-2 font-bold transition-all hover:opacity-90"
                                    style={{
                                        height: '48px',
                                        borderRadius: '9999px',
                                        background: '#0F1DD7',
                                        color: '#fff',
                                        fontSize: '16px',
                                        marginTop: '4px',
                                    }}
                                >
                                    {submitting ? 'Enviando...' : 'Enviar mensagem'}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                                </button>

                                {/* Privacy text */}
                                <p className="text-center text-[#6B7280]" style={{ fontSize: '13px' }}>
                                    Ao enviar, você concorda com nossa{' '}
                                    <a href="/privacidade-empresa" className="text-[#0F1DD7] underline hover:opacity-80">
                                        Política de Privacidade
                                    </a>
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </section>
    );
};

export default ContactForm;
